import { useRef, useLayoutEffect } from 'react';

// Minimal FLIP implementation for animating grid item position changes.
//
// Usage:
//   const setItemRef = useFLIPGrid(itemKeys);
//   {items.map(item => <div key={item.id} ref={setItemRef(item.id)}>...</div>)}
//
// When the supplied key signal changes, we measure each tracked element's
// bounding rect. If a rect existed for the same key in the previous render,
// we compute the delta, apply an inverse transform synchronously, then
// transition back to identity on the next frame.
//
// Honours prefers-reduced-motion.

const TRANSITION = 'transform 380ms cubic-bezier(0.22, 1, 0.36, 1)';

export default function useFLIPGrid(keysSignal) {
  const elementsRef = useRef(new Map()); // key -> HTMLElement
  const prevRectsRef = useRef(new Map()); // key -> DOMRect

  useLayoutEffect(() => {
    let cancelled = false;
    const frameIds = [];
    const reduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const nextRects = new Map();
    elementsRef.current.forEach((el, key) => {
      if (!el || !el.isConnected) return;
      const rect = el.getBoundingClientRect();
      nextRects.set(key, rect);

      const prevRect = prevRectsRef.current.get(key);
      if (prevRect && !reduced) {
        const dx = prevRect.left - rect.left;
        const dy = prevRect.top - rect.top;
        if (dx !== 0 || dy !== 0) {
          el.style.transition = 'none';
          el.style.transform = `translate(${dx}px, ${dy}px)`;
          // Force reflow so the browser registers the inverse transform
          // before we kick off the animation.
          // eslint-disable-next-line no-unused-expressions
          el.offsetWidth;
          const frameId = requestAnimationFrame(() => {
            if (cancelled || !el.isConnected) return;
            el.style.transition = TRANSITION;
            el.style.transform = '';
          });
          frameIds.push(frameId);
        }
      }
    });

    prevRectsRef.current = nextRects;

    return () => {
      cancelled = true;
      frameIds.forEach((frameId) => cancelAnimationFrame(frameId));
    };
  }, [keysSignal]);

  return (key) => (el) => {
    if (el) {
      elementsRef.current.set(key, el);
    } else {
      elementsRef.current.delete(key);
    }
  };
}
